import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Timer } from "lucide-react";
import { mockDoctors } from "../data/mockData";
import { CASES, GAME_TYPES } from "../data/sporeGames";
import { getProgress, markRevealed, nextSealedId } from "../lib/progress";
import { SporeHeader, SporePage, sporeBtn } from "./SporeShell";
import { RevealScreen, MovedOnScreen } from "./GameOutcome";
import { ScanGame } from "./games/ScanGame";
import { RiddleGame } from "./games/RiddleGame";
import { PurgeGame } from "./games/PurgeGame";
import { MemoryGame } from "./games/MemoryGame";
import { DecodeGame } from "./games/DecodeGame";
import { SignalGame } from "./games/SignalGame";
import { VitalsGame } from "./games/VitalsGame";

const GAMES = { scan: ScanGame, riddle: RiddleGame, purge: PurgeGame, memory: MemoryGame, decode: DecodeGame, signal: SignalGame, vitals: VitalsGame };

const TimerBar = ({ doctor, left, total, penalty }) => {
  const pct = (left / total) * 100;
  const low = left <= 80;
  const color = low ? "bg-[#FF2E63]" : pct < 50 ? "bg-amber-400" : "bg-cyan-400";
  return (
    <div className={`rounded-sm border bg-[#0E131F] p-4 flex items-center gap-4 ${low ? "border-red-500/70 shadow-[0_0_20px_rgba(255,46,99,0.3)]" : "border-slate-800"}`}>
      <img src={doctor.image} alt={doctor.name} className="w-12 h-12 rounded-sm object-cover object-top grayscale contrast-125 border border-slate-700" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 font-tac text-xs sm:text-sm">
          <span className="text-slate-300 truncate">
            <span className="sm:hidden">{doctor.name.split(" ").pop().toUpperCase()} LEAVES IN</span>
            <span className="hidden sm:inline">{doctor.name.toUpperCase()} MOVES ON IN</span>
          </span>
          <span className="relative flex items-center gap-1.5">
            <Timer className={`h-4 w-4 ${low ? "text-red-400" : "text-cyan-300"}`} />
            <span data-testid="game-time-left" className={`tabular-nums text-lg ${low ? "text-red-300 spore-flicker" : "text-cyan-200"}`}>{(left / 10).toFixed(1)}s</span>
            {penalty && (
              <span key={penalty.key} data-testid="game-penalty" className="absolute -top-5 right-0 text-red-400 font-bold spore-float-out">−{penalty.amount}s</span>
            )}
          </span>
        </div>
        <div className="mt-2 h-2.5 bg-slate-950 rounded-full border border-slate-800 overflow-hidden">
          <div className={`h-full ${color} transition-[width,background-color] duration-100`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

const GameSession = ({ doctor, entry }) => {
  const navigate = useNavigate();
  const type = GAME_TYPES[entry.game];
  const Game = GAMES[entry.game];
  const total = type.time * 10;
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState("playing");
  const [left, setLeft] = useState(total);
  const [penalty, setPenalty] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const leftRef = useRef(total);
  const statusRef = useRef("playing");
  leftRef.current = left;

  useEffect(() => {
    if (status !== "playing") return undefined;
    const t = setInterval(() => {
      if (statusRef.current === "playing") setLeft((v) => Math.max(0, v - 1));
    }, 100);
    return () => clearInterval(t);
  }, [status, attempt]);

  useEffect(() => {
    if (statusRef.current === "playing" && left === 0) {
      statusRef.current = "lost";
      setStatus("lost");
    }
  }, [left, status]);

  const onWin = useCallback(() => {
    if (statusRef.current !== "playing") return;
    statusRef.current = "won";
    const s = (total - leftRef.current) / 10;
    markRevealed(doctor.id, s);
    setSeconds(s);
    setTimeout(() => setStatus("won"), 700);
  }, [doctor.id, total]);

  const onPenalty = useCallback((amount) => {
    if (statusRef.current !== "playing") return;
    setLeft((v) => Math.max(0, v - amount * 10));
    setPenalty({ amount, key: Date.now() });
  }, []);

  const retry = () => {
    statusRef.current = "playing";
    setLeft(total);
    setPenalty(null);
    setAttempt((a) => a + 1);
    setStatus("playing");
  };

  const progress = useMemo(() => (status === "won" ? getProgress() : {}), [status]);
  const nextId = status === "won" ? nextSealedId(doctor.id, progress) : null;

  return (
    <SporePage>
      <SporeHeader>
        <button type="button" data-testid="game-exit-btn" onClick={() => navigate(`/doctor/${doctor.id}`)} className={sporeBtn.ghost}>
          <ArrowLeft className="h-3.5 w-3.5" /> Exit test
        </button>
      </SporeHeader>
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        {status === "won" && (
          <RevealScreen
            doctor={doctor}
            entry={entry}
            seconds={seconds}
            best={progress[doctor.id]?.seconds ?? seconds}
            revealedCount={Object.keys(progress).filter((id) => CASES[id]).length}
            total={mockDoctors.length}
            onNext={nextId ? () => navigate(`/doctor/${nextId}`, { replace: true }) : null}
            onReplay={retry}
            onRoster={() => navigate("/")}
          />
        )}
        {status === "lost" && (
          <MovedOnScreen doctor={doctor} onRetry={retry} onBack={() => navigate(`/doctor/${doctor.id}`)} />
        )}
        {status === "playing" && (
          <>
            <TimerBar doctor={doctor} left={left} total={total} penalty={penalty} />
            <section data-testid="game-panel" data-game={entry.game} className="rounded-sm border border-slate-800 bg-[#121824] p-5 sm:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 pb-5 border-b border-slate-800">
                <span data-testid="game-type-badge" className={`self-start font-tac text-xs tracking-widest px-2.5 py-1.5 border rounded-sm ${type.badge}`}>{type.name.toUpperCase()}</span>
                <p className="text-sm text-slate-400">{type.howTo}</p>
              </div>
              <Game key={attempt} entry={entry} onWin={onWin} onPenalty={onPenalty} />
            </section>
          </>
        )}
      </main>
    </SporePage>
  );
};

export default function MiniGame() {
  const { id } = useParams();
  const doctor = mockDoctors.find((d) => d.id === id);
  if (!doctor || !CASES[id]) return <Navigate to="/" replace />;
  return <GameSession key={id} doctor={doctor} entry={CASES[id]} />;
}
